import {
	IdentityProviderProtocol,
	type IdentityProviderResource,
	type OidcProviderConfigurationView,
} from '@today/types'
import { Field } from '@today/ui-components'
import { Button, Tabs } from '@today/ui-primitives'
import { OidcFields } from './oidc-fields.view'
import type { ProviderFormSubmit } from './provider-form.types'
import { SamlFields } from './saml-fields.view'
import { type ProviderFormState, useProviderFormState } from './use-provider-form-state'

/**
 * Props for {@link ProviderForm}.
 *
 * @property provider - The provider to edit, or `null` to create one.
 * @property isBusy - True while a request is in flight; submit is disabled.
 * @property error - A refusal to announce (e.g. invalid metadata, a taken code).
 * @property onSubmit - Reports the create or edit. On an edit, `configuration` is omitted when only the name changed.
 * @property onCancel - Closes the form.
 */
export interface ProviderFormProps {
	provider: IdentityProviderResource | null
	isBusy?: boolean
	error?: string | null
	onSubmit: (input: ProviderFormSubmit) => void
	onCancel: () => void
}

const PROTOCOL_LABEL: Record<IdentityProviderProtocol, string> = {
	[IdentityProviderProtocol.SAML2]: 'SAML 2.0',
	[IdentityProviderProtocol.OIDC]: 'OpenID Connect',
}

/** The protocol-specific inputs. */
function ProtocolFields({
	form,
	provider,
	isBusy,
}: {
	form: ProviderFormState
	provider: IdentityProviderResource | null
	isBusy: boolean
}) {
	const protocol = form.protocol
	if (protocol === IdentityProviderProtocol.SAML2) {
		return <SamlFields draft={form.saml} onChange={form.updateSaml} isEdit={provider !== null} isDisabled={isBusy} />
	}
	const secretSet = provider ? (provider.configuration as OidcProviderConfigurationView).clientSecretSet : false
	return <OidcFields draft={form.oidc} onChange={form.updateOidc} clientSecretSet={secretSet} isDisabled={isBusy} />
}

/** Code and name. The code is fixed once created. */
function IdentityFields({ form, isEdit, isBusy }: { form: ProviderFormState; isEdit: boolean; isBusy: boolean }) {
	return (
		<div className="flex flex-col gap-3">
			<Field
				label="Code"
				description="Lowercase letters, digits and hyphens. It cannot be changed later."
				value={form.code}
				readOnly={isEdit}
				disabled={isBusy}
				onChange={(event) => form.setCode(event.target.value)}
			/>
			<Field
				label="Display name"
				value={form.name}
				disabled={isBusy}
				onChange={(event) => form.setName(event.target.value)}
			/>
		</div>
	)
}

/**
 * Create or edit one tenant identity provider (TO-410 REQ-003 / REQ-004 /
 * REQ-006): the protocol (fixed once created), SAML with a metadata-source
 * switcher, or OIDC with a write-only client secret.
 *
 * Pure: the page's hook sends the request. Every rule (metadata validity, the
 * mapping guard, the OIDC gate, code uniqueness) is the server's, and a refusal
 * arrives here as `error`.
 */
export function ProviderForm({ provider, isBusy = false, error, onSubmit, onCancel }: ProviderFormProps) {
	const form = useProviderFormState(provider)
	const isEdit = provider !== null
	const fields = <ProtocolFields form={form} provider={provider} isBusy={isBusy} />

	return (
		<section className="flex flex-col gap-4" aria-label={isEdit ? `Edit ${provider.name}` : 'Add identity provider'}>
			<h3 className="text-ds-base font-semibold text-ds-text">
				{isEdit ? `Edit ${provider.name}` : 'Add identity provider'}
			</h3>
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
			<IdentityFields form={form} isEdit={isEdit} isBusy={isBusy} />
			{isEdit ? (
				<>
					<p className="text-ds-sm text-ds-text-muted">Protocol: {PROTOCOL_LABEL[form.protocol]}</p>
					{fields}
				</>
			) : (
				<Tabs
					aria-label="Protocol"
					selectedKey={form.protocol}
					onSelectionChange={(key) => form.setProtocol(key as IdentityProviderProtocol)}
					tabs={Object.values(IdentityProviderProtocol).map((protocol) => ({
						id: protocol,
						label: PROTOCOL_LABEL[protocol],
						content: fields,
					}))}
				/>
			)}
			<div className="flex gap-2">
				<Button variant="primary" isDisabled={isBusy} onPress={() => onSubmit(form.submission())}>
					{isEdit ? 'Save provider' : 'Create provider'}
				</Button>
				<Button variant="secondary" isDisabled={isBusy} onPress={onCancel}>
					Cancel
				</Button>
			</div>
		</section>
	)
}
